import { Component, type ReactNode } from "react";
import { Center, Stack, Title, Text, Button, Code, Alert } from "@mantine/core";
import { IconAlertTriangle, IconRefresh } from "@tabler/icons-react";

interface State {
  error: Error | null;
}

interface Props {
  children: ReactNode;
}

// Class component because React 18 still only allows class-based error
// boundaries. Catches any render-time exception in the subtree and shows a
// recoverable fallback instead of letting the whole document go blank.
export default class ErrorBoundary extends Component<Props, State> {
  state: State = { error: null };

  static getDerivedStateFromError(error: Error): State {
    return { error };
  }

  componentDidCatch(error: Error, info: { componentStack?: string }) {
    // Surface the trace so the user can paste it back to us.
    console.error("ErrorBoundary caught", error, info);
  }

  handleReset = () => {
    this.setState({ error: null });
  };

  handleReload = () => {
    window.location.reload();
  };

  render() {
    if (!this.state.error) return this.props.children;
    const e = this.state.error;
    return (
      <Center mih="100vh" px="md">
        <Stack gap="md" style={{ maxWidth: 560 }}>
          <Alert color="red" icon={<IconAlertTriangle size={20} />} variant="light" title="Something went wrong">
            <Text size="sm">
              CoreWatch hit an unexpected error and stopped rendering this view. Your data is safe — only this
              browser tab is affected.
            </Text>
          </Alert>
          <Title order={4}>What happened</Title>
          <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word" }}>
            {e.name}: {e.message}
          </Code>
          {e.stack ? (
            <Code block style={{ whiteSpace: "pre-wrap", wordBreak: "break-word", maxHeight: 200, overflow: "auto" }}>
              {e.stack.split("\n").slice(0, 12).join("\n")}
            </Code>
          ) : null}
          <Stack gap={6}>
            <Button leftSection={<IconRefresh size={14} />} onClick={this.handleReload}>
              Reload the dashboard
            </Button>
            <Button variant="default" onClick={this.handleReset}>
              Try again without reloading
            </Button>
          </Stack>
        </Stack>
      </Center>
    );
  }
}
